import {
  DEFAULT_DATE_TIME_FORMAT,
  SERVER_DATE_FORMAT,
  addDays,
  convertDateToServerFormat,
  formatInTimeZone,
  getFormattedDateString,
  parseISO,
  zonedTimeToUtc,
} from 'src/utils/date';

const UTC_TIME_ZONE = 'UTC';
const PAY_PERIOD_TIME_ZONE = 'America/New_York';
const PAY_PERIOD_CLOSES_AT_FORMAT = 'EEEE, MMMM dd, h:mm aaaa';
const EASTERN_PAY_PERIOD_CLOSES_AT_FORMAT = `${PAY_PERIOD_CLOSES_AT_FORMAT} 'ET'`;
const PAY_PERIOD_MOMENT_FORMAT = `${DEFAULT_DATE_TIME_FORMAT} 'ET'`;

export const addDaysToDate = (date: string, amount: number): string =>
  convertDateToServerFormat(addDays(parseISO(date), amount));

export const getPayPeriodToday = (weeklyPayPeriodsStartDate: string, now: Date = new Date()): string => {
  const utcToday = formatInTimeZone(now, UTC_TIME_ZONE, SERVER_DATE_FORMAT);
  const easternToday = formatInTimeZone(now, PAY_PERIOD_TIME_ZONE, SERVER_DATE_FORMAT);
  if (easternToday >= weeklyPayPeriodsStartDate) return easternToday;

  const lastDayBeforeWeeklyPayPeriods = addDaysToDate(weeklyPayPeriodsStartDate, -1);
  return utcToday < lastDayBeforeWeeklyPayPeriods ? utcToday : lastDayBeforeWeeklyPayPeriods;
};

const isEasternBoundary = (day: string, weeklyPayPeriodsStartDate: string): boolean => day >= weeklyPayPeriodsStartDate;

const getPayPeriodBoundary = (day: string, weeklyPayPeriodsStartDate: string): Date =>
  zonedTimeToUtc(
    `${day}T00:00:00`,
    isEasternBoundary(day, weeklyPayPeriodsStartDate) ? PAY_PERIOD_TIME_ZONE : UTC_TIME_ZONE,
  );

export const getPayPeriodStartsAt = (firstDay: string, weeklyPayPeriodsStartDate: string): Date =>
  getPayPeriodBoundary(firstDay, weeklyPayPeriodsStartDate);

export const getPayPeriodEndsAt = (lastDay: string, weeklyPayPeriodsStartDate: string): Date =>
  getPayPeriodBoundary(addDaysToDate(lastDay, 1), weeklyPayPeriodsStartDate);

export const formatPayPeriodClosesAt = (lastDay: string, weeklyPayPeriodsStartDate: string): string => {
  const endsAt = getPayPeriodEndsAt(lastDay, weeklyPayPeriodsStartDate);
  if (!isEasternBoundary(addDaysToDate(lastDay, 1), weeklyPayPeriodsStartDate)) {
    return getFormattedDateString(endsAt, PAY_PERIOD_CLOSES_AT_FORMAT);
  }

  return formatInTimeZone(new Date(endsAt.getTime() - 1000), PAY_PERIOD_TIME_ZONE, EASTERN_PAY_PERIOD_CLOSES_AT_FORMAT);
};

export const formatPayPeriodMoment = (moment: string): string =>
  formatInTimeZone(parseISO(moment), PAY_PERIOD_TIME_ZONE, PAY_PERIOD_MOMENT_FORMAT);
